/**
 * Custom keywords: static placeholders that authors define once per event and use in any
 * content text (meeting point, contact person, sponsor name, ...).
 *
 * Port of the app reference implementation (EspotoProject:
 * SharedCode/src/commonMain/kotlin/system/CustomKeywords.kt).
 *
 * Definitions are delivered by the server as a structured `custom_keywords` field in the
 * event checkin response (array of {name, content}, language-resolved server-side; see
 * EspotoServer docs/keywords.md). On ingestion of the event response the definitions are
 * normalized into a map and stored in the events store module; the usage tokens
 * `[custom:<name>]` are then substituted once at load time in every ingested text (event
 * description, custom contents, finish text, spot texts, hints, solutions) — not at
 * render time. The values are static, so load-time substitution covers every display
 * path automatically.
 *
 * Deliberately independent from the `show_*` keyword engine in keywords.mjs: values
 * may contain `show_*` keywords, which are substituted later at render time as usual
 * (load happens before render, so the order works out by itself). The `show_*` scanner
 * only reacts to `[show_` tokens and passes the usage syntax through untouched.
 *
 * Semantics:
 * - Substitution is a literal single pass: inserted values are never rescanned, so
 *   there is no custom-in-custom, no recursion and no cycle handling.
 * - Names are matched literally (case-sensitive); convention is lowercase.
 * - A used token without definition stays visibly raw (shows up in the test run
 *   instead of silently disappearing).
 * - Value changes only take effect on the next full event load (snapshot semantics).
 */

const USAGE_TOKEN_PREFIX = '[custom:';

/**
 * Normalizes the `custom_keywords` response field (array of {name, content}) into the
 * name→content map used by replaceCustomKeywords. Defensive against malformed input:
 * non-arrays yield an empty map, entries without a usable name are skipped, the first
 * entry wins on duplicate names.
 */
export function normalizeCustomKeywordDefinitions(list) {
    const definitions = {};
    if (!Array.isArray(list)) {
        return definitions;
    }
    for (const entry of list) {
        if (!entry || typeof entry.name !== 'string' || entry.name.length === 0) {
            continue;
        }
        if (!Object.prototype.hasOwnProperty.call(definitions, entry.name)) {
            definitions[entry.name] = typeof entry.content === 'string' ? entry.content : '';
        }
    }
    return definitions;
}

/**
 * Substitutes every `[custom:<name>]` usage token in html with its defined value —
 * one literal pass, inserted values are never rescanned (no custom-in-custom). Tokens
 * whose name has no definition stay raw.
 */
export function replaceCustomKeywords(html, definitions) {
    if (!definitions || isEmpty(definitions) || html.indexOf(USAGE_TOKEN_PREFIX) === -1) {
        return html;
    }

    let out = '';
    let i = 0;
    while (i < html.length) {
        const open = html.indexOf(USAGE_TOKEN_PREFIX, i);
        if (open === -1) {
            out += html.substring(i);
            break;
        }
        out += html.substring(i, open);

        const close = html.indexOf(']', open);
        let value = null;
        if (close !== -1) {
            const name = html.substring(open + USAGE_TOKEN_PREFIX.length, close);
            if (Object.prototype.hasOwnProperty.call(definitions, name)) {
                value = definitions[name];
            }
        }
        if (value !== null) {
            out += value;
            i = close + 1;
        } else {
            // unknown name or no closer: keep the token visibly raw and rescan right
            // behind the '[' so a valid token starting inside is still found
            out += '[';
            i = open + 1;
        }
    }
    return out;
}

function isEmpty(obj) {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            return false;
        }
    }
    return true;
}
